import { useQueryClient } from '@tanstack/react-query';
import { createNavigationContainerRef } from '@react-navigation/native';
import React, { useImperativeHandle, forwardRef, useMemo } from 'react';
import {
  useForm,
  FormProvider,
  type FieldErrors,
  type Path,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { View } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import type { Task } from '@/common/api/models/task';
import type { ModuleLecturerResponse } from '@/common/api/models/moduleLecturerResponse';
import {
  getGetTaskByModuleVersionQueryKey,
} from '@/common/api/endpoints/tasks/tasks';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useModuleEditorStore } from '@/lecturer/ModuleEditor/stores/ModuleEditorStore';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useUpdateMcqConfig } from '@/common/api/endpoints/mcq/mcq';
import type { MCQConfigInput } from '@/common/api/models/mCQConfigInput';
import { useDialog } from '@/common/context/DialogContext';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import log from '@/common/utils/logger';
import { summarizeRhfFieldErrors } from '@/common/utils/summarizeRhfFieldErrors';
import TaskFormNavigator from './TaskFormNavigator';
import { taskFormSchema, type TaskFormData } from './utils/taskEditorSchemas';
import {
  resolveTaskEditorTabForField,
  TASK_EDITOR_GRADING_RUBRIC_FEATURE_FLAG,
  type TaskFormTabsParamList,
} from './utils/taskFormTabsNavigation';
import { toTaskFormData, toTaskApiRequest } from './utils/taskEditorMappers';
import { EVENT_KIND_IMMUTABLE_ERROR_CODE } from './utils/eventKindSticky';

export interface TaskEditorHandle {
  save: () => void;
}

interface TaskEditorProps {
  task: Task;
  module: ModuleLecturerResponse;
  isReadOnly?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
  testID?: string;
}

const TaskEditorInternal = forwardRef<TaskEditorHandle, TaskEditorProps>(({
  task,
  module,
  isReadOnly = false,
  onDirtyChange,
  onSavingChange,
}, ref) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const { updateTaskMutation, tasksQueryKey } = useLecturerModuleData(module.id, module.version, task.id);
  const setSelectedTaskId = useModuleEditorStore((state) => state.setSelectedTaskId);
  const updateMcqConfigMutation = useUpdateMcqConfig();
  const queryClient = useQueryClient();
  const taskFormNavigationRef = useMemo(
    () => createNavigationContainerRef<TaskFormTabsParamList>(),
    [],
  );
  const { isFeatureActive } = useUserFeatures();
  const { editableDimensionKeys } = useTenantGradingSettings();

  // Report saving state to parent
  React.useEffect(() => {
    onSavingChange?.(updateTaskMutation.isPending || updateMcqConfigMutation.isPending);
  }, [updateTaskMutation.isPending, updateMcqConfigMutation.isPending, onSavingChange]);

  const methods = useForm<TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: toTaskFormData(task) as TaskFormData,
    mode: 'onBlur',
  });

  const {
    handleSubmit, reset, getValues, formState, setFocus, setValue,
  } = methods;

  // When the task identity changes (e.g. navigating to a different task), reset the form.
  // We intentionally do NOT reset on every task prop change so that the optimistic cache
  // update from updateTaskMutation does not wipe out the user's in-progress edits.
  const taskId = task.id;
  React.useEffect(() => {
    reset(toTaskFormData(task) as TaskFormData, { keepDirty: false, keepTouched: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Re-seed the form when a background job (e.g. "generate missing rubrics") adds a
  // rubric or objectives to this task after a cache refetch. Guarded by !isDirty so a
  // refetch never clobbers the lecturer's in-progress edits.
  const hasRubric = !!task.rubric?.dimensions?.length;
  const hasObjectives = !!task.analysis?.dimensions && Object.keys(task.analysis.dimensions).length > 0;
  React.useEffect(() => {
    if (formState.isDirty) return;
    reset(toTaskFormData(task) as TaskFormData, { keepDirty: false, keepTouched: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRubric, hasObjectives]);

  // Report dirty state to parent
  React.useEffect(() => {
    onDirtyChange?.(formState.isDirty);
  }, [formState.isDirty, onDirtyChange]);

  const onSubmit = async (data: TaskFormData) => {
    if (isReadOnly) {
      // Previously this silently no-op'd. That meant a user who somehow
      // managed to dirty the form (e.g. via a side-effect like the formal
      // dimension auto-add in the rubric tab) could click Save, see no
      // feedback, and lose their edits on navigation.
      log.warn('[TaskEditor] Save attempted while editor is read-only; ignoring.');
      showToastMessage('error', t('lecturer.moduleEditor.taskEditor.messages.readOnlySaveBlocked'));
      return;
    }

    // Transform form data back to API structure
    const updateRequest = toTaskApiRequest(data, editableDimensionKeys);

    const previousTaskId = task.id;

    try {
      const response = await updateTaskMutation.mutateAsync({
        moduleId: module.id,
        version: module.version,
        taskId: previousTaskId,
        data: updateRequest,
      });

      const savedTask = response?.data?.data;
      const reminted = !!savedTask && (
        savedTask.id !== previousTaskId || !!savedTask.remintedFromTaskId
      );

      if (reminted && savedTask) {
        setSelectedTaskId(savedTask.id);
        await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
        await queryClient.invalidateQueries({
          queryKey: getGetTaskByModuleVersionQueryKey(module.id, module.version, previousTaskId),
        });
        await queryClient.invalidateQueries({
          queryKey: getGetTaskByModuleVersionQueryKey(module.id, module.version, savedTask.id),
        });
        showToastMessage(
          'success',
          t('lecturer.moduleEditor.taskEditor.messages.typeChangeRemintSuccess'),
        );
        reset(toTaskFormData(savedTask) as TaskFormData, { keepDirty: false, keepTouched: false });
        return;
      }

      if (task.type === 'multiple_choice' && data.mcqConfig) {
        const questions = task.mcqConfig?.questions ?? [];
        const mcqPayload: MCQConfigInput = {
          questions,
          questionsToShow: data.mcqConfig.questionsToShow,
          passThresholdPercent: data.mcqConfig.passThresholdPercent,
          shuffleQuestions: data.mcqConfig.shuffleQuestions,
          shuffleOptions: data.mcqConfig.shuffleOptions,
          showImmediateFeedback: data.mcqConfig.showImmediateFeedback,
          timeLimitMinutes: data.mcqConfig.timeLimitMinutes,
        };

        await updateMcqConfigMutation.mutateAsync({
          moduleId: module.id,
          version: module.version,
          taskId: previousTaskId,
          data: mcqPayload,
        });

        // Invalidate tasks query explicitly because MCQ update does not do it automatically
        await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      }

      showToastMessage('success', t('lecturer.moduleEditor.taskEditor.messages.saveSuccess'));
      reset(getValues(), { keepDirty: false, keepTouched: false });
    } catch (error) {
      const errorDetails = reportApiError(
        error as Error,
        t('lecturer.moduleEditor.taskEditor.buttons.save'),
        'toast',
        { expectedErrorCodes: [EVENT_KIND_IMMUTABLE_ERROR_CODE] },
      );
      if (errorDetails.code === EVENT_KIND_IMMUTABLE_ERROR_CODE) {
        const savedEventKindId = task.eventConfig?.eventKindId;
        if (savedEventKindId) {
          setValue('eventConfig.eventKindId', savedEventKindId, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
      }
    }
  };

  useImperativeHandle(ref, () => ({
    save: () => {
      handleSubmit(onSubmit, (errors: FieldErrors<TaskFormData>) => {
        log.info('[TaskEditor] Validation errors:', errors);
        const { summary, firstFieldPath } = summarizeRhfFieldErrors(errors);
        const message = summary
          ? t('lecturer.moduleEditor.taskEditor.messages.formErrorWithDetails', { details: summary })
          : t('lecturer.moduleEditor.taskEditor.messages.formError');
        showToastMessage('error', message);
        if (firstFieldPath) {
          const taskType = getValues('type');
          const tab = resolveTaskEditorTabForField(firstFieldPath, {
            isActivity: taskType === 'activity',
            isEssay: taskType === 'essay',
            isMCQ: taskType === 'multiple_choice',
            isOralExam: taskType === 'oral_exam',
            gradingRubricEnabled: isFeatureActive(TASK_EDITOR_GRADING_RUBRIC_FEATURE_FLAG),
            aiEnabled: isFeatureActive('lecturer-task-ai'),
          });
          if (taskFormNavigationRef.isReady()) {
            taskFormNavigationRef.navigate(tab);
          }
          const path = firstFieldPath as Path<TaskFormData>;
          setTimeout(() => {
            setFocus(path);
          }, 150);
        }
      })();
    },
  }));

  return (
    <FormProvider {...methods}>
      <View flex={1} bg="$bgApp" testID="task-editor">
        <TaskFormNavigator
          task={task}
          module={module}
          isReadOnly={isReadOnly}
          navigationRef={taskFormNavigationRef}
        />
      </View>
    </FormProvider>
  );
});

const TaskEditor = React.memo(TaskEditorInternal);

TaskEditor.displayName = 'TaskEditor';

export default TaskEditor;
